"use client";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

const founders = [
  {
    name: "Barbara Diogo",
    role: "Fundadora",
    bio: "Formada em Gestão de Turismo, com MBA em Marketing Estratégico, Mestrado e Doutorado em Administração e Marketing pela PUC/RS, Barbara Diogo reúne quase 15 anos de experiência no setor. Atuou em hotéis e agências de viagem, foi professora universitária e integra o grupo de pesquisa em Comportamento do Consumidor da PUC/RS. Morou três anos na Irlanda, ampliando sua visão global sobre turismo, marketing e comportamento de viajantes. Na ESCAPA!, atua em consultorias de marketing, palestras, cursos e projetos de inovação em turismo, com especial foco no uso da Inteligência Artificial.",
    img: "barbara.jpg",
    objectPosition: "50% 60%",
    width: 600,
    height: 900,
    icon: "barbara_icon.jpg",
  },
  {
    name: "Thamiris Magalhães",
    role: "Fundadora",
    bio: "Jornalista, Mestre em Comunicação pela Unisinos/RS e Doutora em Administração/Marketing pela ESPM/SP, Thamiris Magalhães possui um período de estágio doutoral na KEDGE Business School, em Bordeaux, onde se aprofundou em Marketing de Luxo. Com mais de 10 anos de docência, soma experiências em agências, jornais e universidades, além de ser autora de pesquisas de alto impacto publicadas em alguns dos maiores periódicos do mundo, como o Journal of Services Marketing e o Marketing Theory Journal. Na ESCAPA!, lidera consultorias, cursos e projetos de inovação em turismo, aproximando ciência, mercado e tecnologia de forma didática e acessível.",
    img: "thamiris.jpg",
    objectPosition: "50% 25%",
    width: 1969,
    height: 2953,
    icon: "thamiris.jpg",
  },
];

const Founders = () => {
  const [selectedFounder, setSelectedFounder] = useState<
    null | (typeof founders)[0]
  >(null);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setSelectedFounder(null);
    }
  };

  useEffect(() => {
    if (selectedFounder) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedFounder]);

  return (
    <>
      <motion.h3
        className="text-xl font-bold"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5 }}
      >
        Nossas fundadoras
      </motion.h3>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5 }}
        className="flex flex-wrap justify-center w-full gap-4"
      >
        {founders.map((founder, i) => (
          <div
            key={i}
            className={`flex flex-col items-center max-w-xs bg-white/80 backdrop-blur-sm border border-gray-200 transition-all cursor-pointer`}
          >
            <div className="relative w-full h-full mb-4 overflow-hidden">
              <Image
                src={`/fundadoras/${founder.img}`}
                alt={`Fundadora ${founder.name}`}
                height={founder.height}
                width={founder.width}
                style={{
                  objectFit: "cover",
                  objectPosition: founder.objectPosition,
                }}
                quality={100}
              />
            </div>
            <div className="p-4">
              <p className="text-lg font-semibold text-gray-900">
                {founder.name}
              </p>
              <span className="text-sm text-gray-500 mb-2">{founder.role}</span>
              <p className="text-sm text-gray-700 text-center leading-relaxed line-clamp-3">
                {founder.bio}
              </p>
              <button
                onClick={() => setSelectedFounder(founder)}
                className="mt-3 text-sm font-medium text-blue-600 hover:underline"
              >
                Ver mais
              </button>
            </div>
          </div>
        ))}

        <AnimatePresence>
          {selectedFounder && (
            <motion.div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 relative md:mx-0 mx-4"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <button
                  onClick={() => setSelectedFounder(null)}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                >
                  <X size={24} className="cursor-pointer" />
                </button>
                <div className="flex flex-col items-center">
                  <div className="h-32 w-32 rounded-full overflow-hidden relative mb-4">
                    <Image
                      src={`/fundadoras/${
                        selectedFounder.icon ?? selectedFounder.img
                      }`}
                      alt={`Fundadora ${selectedFounder.name}`}
                      height={selectedFounder.height}
                      width={selectedFounder.width}
                      style={{
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                      quality={100}
                    />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedFounder.name}
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    {selectedFounder.role}
                  </p>
                  <p className="text-sm text-gray-700 text-center leading-relaxed">
                    {selectedFounder.bio}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default Founders;
